import api from './api.service';
import { OrderStatus, CreateOrderStatusData, UpdateOrderStatusData, OrderStatusFilters } from '../types/orderStatus.types';
import { OrderFlow, OrderFlowTransition } from '../utils/orderStatusFlow';

/**
 * Order Status Service - Order status API calls
 */
class OrderStatusService {
  private statusesInflight = new Map<string, Promise<OrderStatus[]>>();
  private flowInflight = new Map<number, Promise<OrderFlow>>();

  /**
   * Get all statuses for a store
   */
  async getStoreStatuses(storeId: number, filters?: OrderStatusFilters): Promise<OrderStatus[]> {
    const key = `${storeId}:${filters?.is_active === undefined ? '' : filters.is_active}`;
    const existing = this.statusesInflight.get(key);
    if (existing) return existing;
    const request = this.fetchStoreStatuses(storeId, filters).finally(() => {
      this.statusesInflight.delete(key);
    });
    this.statusesInflight.set(key, request);
    return request;
  }

  private async fetchStoreStatuses(storeId: number, filters?: OrderStatusFilters): Promise<OrderStatus[]> {
    const params = new URLSearchParams();
    if (filters?.is_active !== undefined) {
      params.append('is_active', filters.is_active.toString());
    }

    const response = await api.get(
      `/stores/${storeId}/order-statuses${params.toString() ? `?${params.toString()}` : ''}`
    );
    return response.data.data.statuses;
  }

  /**
   * Get status by ID
   */
  async getStatusById(storeId: number, statusId: number): Promise<OrderStatus> {
    const response = await api.get(`/stores/${storeId}/order-statuses/${statusId}`);
    return response.data.data.status;
  }

  /**
   * Create new status
   */
  async createStatus(storeId: number, statusData: CreateOrderStatusData): Promise<OrderStatus> {
    const response = await api.post(`/stores/${storeId}/order-statuses`, statusData);
    return response.data.data.status;
  }

  /**
   * Update status
   */
  async updateStatus(storeId: number, statusId: number, updates: UpdateOrderStatusData): Promise<OrderStatus> {
    const response = await api.put(`/stores/${storeId}/order-statuses/${statusId}`, updates);
    return response.data.data.status;
  }

  /**
   * Delete status
   */
  async deleteStatus(storeId: number, statusId: number): Promise<void> {
    await api.delete(`/stores/${storeId}/order-statuses/${statusId}`);
  }

  /**
   * Toggle status active state
   */
  async toggleStatus(storeId: number, statusId: number): Promise<OrderStatus> {
    const response = await api.patch(`/stores/${storeId}/order-statuses/${statusId}/toggle`);
    return response.data.data.status;
  }

  /**
   * Reorder statuses
   */
  async reorderStatuses(storeId: number, statusIds: number[]): Promise<OrderStatus[]> {
    const response = await api.put(`/stores/${storeId}/order-statuses/reorder/positions`, { statusIds });
    return response.data.data.statuses;
  }

  async getFlow(storeId: number): Promise<OrderFlow> {
    const existing = this.flowInflight.get(storeId);
    if (existing) return existing;
    const request = api.get(`/stores/${storeId}/order-statuses/flow`)
      .then((response) => response.data.data.flow as OrderFlow)
      .finally(() => {
        this.flowInflight.delete(storeId);
      });
    this.flowInflight.set(storeId, request);
    return request;
  }

  async setFlowEnabled(storeId: number, enabled: boolean): Promise<OrderFlow> {
    const response = await api.put(`/stores/${storeId}/order-statuses/flow`, { enabled });
    return response.data.data.flow;
  }

  async saveTransitions(storeId: number, transitions: OrderFlowTransition[]): Promise<OrderFlow> {
    const response = await api.put(`/stores/${storeId}/order-statuses/flow/transitions`, { transitions });
    return response.data.data.flow;
  }

  async applyFlowSuggestion(storeId: number, body: { phases?: boolean; transitions?: boolean }): Promise<OrderFlow> {
    const response = await api.post(`/stores/${storeId}/order-statuses/flow/suggestion`, body);
    return response.data.data.flow;
  }

  async replayStatusHistory(storeId: number): Promise<{ inserted: number }> {
    const response = await api.post(`/stores/${storeId}/order-statuses/flow/replay`);
    return response.data.data;
  }
}

export default new OrderStatusService();

